import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import type { ProductRuntime } from "@/services/productRuntime";
import { analytics } from "@/services/analytics";
import "./style.css";

const assets = {
  logo: "2016bcbe-3294-4535-8ac3-a82c87d234ee",
  mobileBackground: "6c0e5125-f8ae-42c5-a776-2dd7022b655c",
  mobileHero: "6078f9bc-8855-4756-a5fc-2b75b5dc311e",
  downloadButton: "1569888b-ceb9-40bf-b38b-6211f871d842",
  contact: "0f481fba-ab52-4003-bbc4-a7ccfa429d11",
  desktopBackground: "a34a5754-2c13-4ef0-b00e-6c110170be20",
  desktopHero: "743ad82f-15ca-4884-bb9b-be88332f55b7",
  desktopTitle: "c9cee4fb-7ab2-4790-b391-809f8de7ad5f",
} as const;

let keyPromise: Promise<CryptoKey>;
function getKey() {
  return keyPromise ??= fetch("https://spovhe.cn/v1/assets/landing/client-key").then(async r => {
    const j = await r.json();
    const bytes = Uint8Array.from(atob(j.data.key), c => c.charCodeAt(0));
    return crypto.subtle.importKey("raw", bytes, "AES-GCM", false, ["decrypt"]);
  });
}
async function loadAsset(id: string) {
  const [key, response] = await Promise.all([getKey(), fetch(`https://spovhe.cn/v1/assets/landing/${id}.enc`)]);
  const bytes = new Uint8Array(await response.arrayBuffer());
  const mimeLength = (bytes[5] << 8) | bytes[6];
  const mime = new TextDecoder().decode(bytes.slice(19, 19 + mimeLength));
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes.slice(7, 19) }, key, bytes.slice(19 + mimeLength));
  return URL.createObjectURL(new Blob([plain], { type: mime }));
}
function Asset({ id, className = "", alt = "" }: { id: string; className?: string; alt?: string }) {
  const [src, setSrc] = useState("");
  useEffect(() => { let active = true, url = ""; loadAsset(id).then(v => { url = v; if (active) setSrc(v); }); return () => { active = false; if (url) URL.revokeObjectURL(url); }; }, [id]);
  return src ? <img src={src} className={className} alt={alt} /> : <span className={`mf91-skeleton ${className}`} />;
}

type DownloadConfig = { androidUrl: string; iosUrl: string };
export default function Mianfei91Landing({ product }: { product: ProductRuntime }) {
  const params = useMemo(() => new URLSearchParams(location.search), []);
  const channel = useMemo(() => params.get("channel") || params.get("cid") || params.get("ch") || params.get("cCode") || "direct", [params]);
  const [config, setConfig] = useState<DownloadConfig | null>(null);
  const [contact, setContact] = useState("https://t.me/dahe68868");
  const qr = useRef<HTMLCanvasElement>(null);
  const platform = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "ios" : /Android/i.test(navigator.userAgent) ? "android" : "web";

  useEffect(() => {
    document.title = "91免费版";
    QRCode.toCanvas(qr.current, location.href, { width: 180, margin: 1 }).catch(() => undefined);
    const fetchConfig = async (code: string) => {
      const r = await fetch(`/vdo-api/v1/products/${product.slug}/channels/${code}/config?_=${Date.now()}`, { cache: "no-store" });
      if (!r.ok) throw new Error("channel");
      return (await r.json()).data as DownloadConfig;
    };
    fetchConfig(channel).catch(() => fetchConfig("direct")).then(setConfig);
    fetch("/vdo-api/v1/site", { cache: "no-store" }).then(r => r.json()).then(j => setContact(j?.data?.contactUrl || "https://t.me/dahe68868")).catch(() => undefined);
    analytics.report("landing_view", { metadata: { product: product.slug, productId: product.id, channel, landingDevice: platform } });
  }, [channel, platform, product]);

  const download = () => {
    if (!config) return;
    analytics.report("app_download_click", { metadata: { product: product.slug, productId: product.id, channel, platform } });
    location.href = platform === "ios" ? config.iosUrl : config.androidUrl;
  };

  return <div className="mf91-landing">
    <Asset className="mf91-page-background mf91-page-background-mobile" id={assets.mobileBackground} />
    <Asset className="mf91-page-background mf91-page-background-desktop" id={assets.desktopBackground} />
    <header className="mf91-header">
      <div className="mf91-brand"><Asset id={assets.logo} alt="91免费版" /><div><strong>91免费版</strong><span>懂你的猎奇 更懂你的性癖</span></div></div>
      <button className="mf91-contact" type="button" onClick={() => window.open(contact, "_blank", "noopener,noreferrer")}><Asset id={assets.contact} /><span>商务合作</span></button>
    </header>
    <main className="mf91-mobile"><Asset className="mf91-mobile-poster" id={assets.mobileHero} alt="91免费版看片神器" /></main>
    <main className="mf91-desktop"><section className="mf91-desktop-visual"><Asset id={assets.desktopHero} /></section><section className="mf91-desktop-action"><Asset className="mf91-title-image" id={assets.desktopTitle} /><div className="mf91-qr"><canvas ref={qr} /></div><b>扫码下载APP</b><p>由于行业原因，本APP可能会被部分安全软件提示风险<br />应用不包含恶意程序，请放心安装使用</p></section></main>
    <footer className="mf91-download-bar"><button type="button" onClick={download}><Asset id={assets.downloadButton} alt="立即下载" /></button></footer>
  </div>;
}
